/**
 * Predictive reorder (flag `predictive_reorder`, plan `advancedAnalytics`;
 * MOT-149): the `/store/reorder-suggestions` client shared by the home card,
 * the Sales Report table and the Store Settings card.
 */
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';

export type ReorderStatus = 'reorder_now' | 'watch' | 'ok' | 'no_count';

export interface ReorderSuggestion {
  productId: string;
  name: string;
  brand: string;
  brandId: string;
  sku: string | null;
  velocityPerDay: number;
  unitsSoldWindow: number;
  unitsSoldRecent: number;
  countedOnHand: number | null;
  countedAt: string | null;
  countStale: boolean;
  soldSinceCount: number;
  /** Where `onHand` came from (MOT-395); present only when the store opted in to the Shopify read. */
  onHandSource?: 'count' | 'shopify' | null;
  /** Shopify's `available` total at the last read, as read. */
  platformOnHand?: number | null;
  platformOnHandAt?: string | null;
  onHand: number | null;
  leadTimeDays: number;
  safetyStockDays: number;
  daysOfSupply: number | null;
  reorderPoint: number;
  suggestedQty: number | null;
  status: ReorderStatus;
}

export interface ReorderSettings {
  organizationId: string;
  defaultLeadTimeDays: number;
  safetyStockDays: number;
  brandLeadTimeDays: Record<string, number>;
  /** Read Shopify stock where the store has no count (MOT-395). */
  useShopifyOnHand?: boolean;
}

export type PlatformOnHandStatus = 'off' | 'ok' | 'reconnect_required' | 'no_integration' | 'error';

export interface PlatformOnHandSummary {
  source: 'shopify';
  status: PlatformOnHandStatus;
  integrationIds: string[];
  fetchedAt: string | null;
  products: number;
  truncated: boolean;
  missingScopes?: string[];
  error?: string;
}

export interface ReorderSettingsPayload {
  settings: ReorderSettings;
  brands: Array<{ brandId: string; brandName: string }>;
}

export interface ReorderSuggestionsResult {
  /** One page of the ranked list. */
  suggestions: ReorderSuggestion[];
  /** Rows in the whole ranked list; `counts` sums to it. */
  total: number;
  offset: number;
  limit: number;
  /** Offset of the next page; `null` on the last page. */
  nextOffset: number | null;
  settings: ReorderSettings;
  window: { start: string; end: string; days: number; recentDays: number; recentWeight: number };
  /** `null` when the store syncs its whole catalog (`syncAll`). */
  selectionSize: number | null;
  syncAll: boolean;
  quietProducts: number;
  counts: { reorderNow: number; watch: number; ok: number; noCount: number };
  /** The Shopify on-hand read; absent unless the store opted in. */
  platformOnHand?: PlatformOnHandSummary;
  /** The location this view is for (MOT-396); absent for the whole store. */
  location?: { locationId: string; name: string; isPrimary: boolean; unlocatedLines: number };
  /** Locations the member may pick. */
  locations?: Array<{ id: string; name: string; isPrimary: boolean }>;
  /** The member is scoped to locations: never offer the whole store. */
  locationScoped?: boolean;
}

/** The change recap's reorder entry (MOT-396). */
export interface ReorderRecapEntry {
  since: string;
  until: string;
  reorderNow: number;
  crossed: number;
  products: Array<{
    productId: string;
    name: string;
    brand: string;
    sku: string | null;
    onHand: number | null;
    reorderPoint: number;
    suggestedQty: number | null;
    daysOfSupply: number | null;
  }>;
  locationId?: string;
}

/** Most counts one `PUT /counts` takes; the table splits a bigger save into requests of this size. */
export const REORDER_COUNTS_PER_WRITE = 500;
/** Rows per page of the Sales Report table. */
export const REORDER_PAGE_SIZE = 200;

export const REORDER_SUGGESTIONS_QUERY_KEY = ['store-reorder-suggestions'] as const;
export const REORDER_SETTINGS_QUERY_KEY = ['store-reorder-settings'] as const;

const ENDPOINT = '/store/reorder-suggestions';

/** The plan gate's 403: the flag is on but the plan does not include Advanced analytics. */
export class ReorderPlanLockedError extends Error {
  readonly planLocked = true;
  constructor(message: string) {
    super(message);
    this.name = 'ReorderPlanLockedError';
  }
}

export const isReorderPlanLocked = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && (error as { planLocked?: boolean }).planLocked === true);

async function readBody<T>(response: Response, fallback: string): Promise<T> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    const message: string = body?.error?.message || body?.message || fallback;
    if (response.status === 403 && /not included in your current plan/i.test(message)) {
      throw new ReorderPlanLockedError(message);
    }
    throw new Error(message);
  }
  return body.data as T;
}

const jsonHeaders = async () => ({ ...(await getAuthHeaders()), 'Content-Type': 'application/json' });

export const reorderApi = {
  async listSuggestions(
    fallback: string,
    opts: { integrationId?: string | null; locationId?: string | null; limit?: number; offset?: number } = {},
  ): Promise<ReorderSuggestionsResult> {
    const params = new URLSearchParams();
    if (opts.integrationId) params.set('integrationId', opts.integrationId);
    if (opts.locationId) params.set('locationId', opts.locationId);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return readBody(await makeApiCall(`${ENDPOINT}${qs ? `?${qs}` : ''}`, { headers: await getAuthHeaders() }), fallback);
  },

  async getSettings(fallback: string): Promise<ReorderSettingsPayload> {
    return readBody(await makeApiCall(`${ENDPOINT}/settings`, { headers: await getAuthHeaders() }), fallback);
  },

  async saveSettings(
    body: { defaultLeadTimeDays?: number; safetyStockDays?: number; brandLeadTimeDays?: Record<string, number | null>; useShopifyOnHand?: boolean },
    fallback: string,
  ): Promise<ReorderSettingsPayload> {
    return readBody(await makeApiCall(`${ENDPOINT}/settings`, { method: 'PUT', headers: await jsonHeaders(), body: JSON.stringify(body) }), fallback);
  },

  /** Counts are for the store as a whole, or for one location; a save bigger than one request allows goes in order, one request at a time. */
  async saveCounts(
    counts: Array<{ productId: string; onHand: number | null }>,
    fallback: string,
    opts: { integrationId?: string | null; locationId?: string | null } = {},
  ): Promise<{ written: number; cleared: number }> {
    const totals = { written: 0, cleared: 0 };
    for (let i = 0; i < counts.length; i += REORDER_COUNTS_PER_WRITE) {
      const body: Record<string, unknown> = { counts: counts.slice(i, i + REORDER_COUNTS_PER_WRITE) };
      if (opts.integrationId) body.integrationId = opts.integrationId;
      if (opts.locationId) body.locationId = opts.locationId;
      const result = await readBody<{ written: number; cleared: number }>(
        await makeApiCall(`${ENDPOINT}/counts`, { method: 'PUT', headers: await jsonHeaders(), body: JSON.stringify(body) }),
        fallback,
      );
      totals.written += result.written;
      totals.cleared += result.cleared;
    }
    return totals;
  },
};
